import Axios from 'axios';
import { useNavigate, useLocation, Navigate, Link } from 'react-router-dom';
import React, { useContext, useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Store } from '../Store';
import { toast } from '../toasts';
import { getError, validateAndClearInvalidToken, getUserInfo } from '../utils';
import { BsFillEyeFill, BsFillEyeSlashFill } from 'react-icons/bs';
import InputGroup from 'react-bootstrap/InputGroup';
import '../App.css';
import { Button, Form, Spinner } from 'react-bootstrap';
import { isTermsOfServiceEnabled, isPrivacyPolicyEnabled, isHomePageEnabled } from '../utils/featureFlags';

export default function SignInScreen({ appName = 'Cuenta' }) {
	const navigate = useNavigate();
	const location = useLocation();
	const from = location.state?.from ? `${location.state.from.pathname}${location.state.from.search || ''}` : '/AdminScreen';
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [passwordType, setPasswordType] = useState('password');
	const [loading, setLoading] = useState(false);
	const isMountedRef = useRef(true);

	const togglePassword = () => {
		if (passwordType === 'password') {
			setPasswordType('text');
			return;
		}
		setPasswordType('password');
	};

	const { dispatch: ctxDispatch } = useContext(Store);

	const submitHandler = async (e) => {
		e.preventDefault();
		setLoading(true);
		try {
			const { data } = await Axios.post('/api/auth/signin', {
				email,
				password,
			});
			ctxDispatch({ type: 'USER_SIGNIN', payload: data });
			localStorage.setItem('userInfo', JSON.stringify(data));
			localStorage.setItem('refreshToken', data.refreshToken);
			navigate(from);
		} catch (err) {
			toast.error(getError(err));
		} finally {
			// Only update loading state if component is still mounted
			if (isMountedRef.current) {
				setLoading(false);
			}
		}
	};

	useEffect(() => {
		isMountedRef.current = true;
		const checkExistingUser = async () => {
			const userInfo = localStorage.getItem('userInfo');
			const refreshToken = localStorage.getItem('refreshToken');

			// Only auto-login if both userInfo and refreshToken exist.
			// This prevents auto-login after logout, when localStorage is cleared.
			if (userInfo && refreshToken) {
				const isValid = await validateAndClearInvalidToken();
				if (isMountedRef.current && isValid) {
					navigate(from);
				}
			}
		};
		checkExistingUser();

		return () => {
			isMountedRef.current = false;
		};
	}, [navigate]);

	if (getUserInfo()) return <Navigate to={from} replace />;

	return (
		<div className='auth-screen sign-in'>
			<Helmet>
				<title>Iniciar sesión</title>
			</Helmet>
			<div className='auth-screen__content'>
				<Form className='auth-card' onSubmit={submitHandler}>
					<h2 className='auth-card__title'>Iniciar sesión</h2>

					<Form.Group className='auth-field' controlId='signin-email'>
						<Form.Label>E-mail</Form.Label>
						<Form.Control
							className='auth-input'
							type='email'
							placeholder='nombre@empresa.com'
							onChange={(e) => setEmail(e.target.value.toLowerCase())}
							required
						/>
					</Form.Group>

					<Form.Group className='auth-field' controlId='signin-password'>
						<Form.Label>Contraseña</Form.Label>
						<InputGroup className='auth-password-group'>
							<Form.Control
								className='auth-input'
								type={passwordType}
								required
								placeholder='••••••••'
								onChange={(e) => setPassword(e.target.value)}
							/>
							<Button
								type='button'
								variant='link'
								className='auth-password-toggle'
								aria-label={passwordType === 'password' ? 'Mostrar contraseña' : 'Ocultar contraseña'}
								onClick={togglePassword}
							>
								{passwordType === 'password' ? <BsFillEyeSlashFill /> : <BsFillEyeFill />}
							</Button>
						</InputGroup>
					</Form.Group>

					<Button className='auth-submit' type='submit' disabled={loading}>
						{loading ? (
							<>
								<Spinner animation='border' size='sm' className='me-1' />
								Ingresando...
							</>
						) : (
							'Entrar'
						)}
					</Button>

					<p className='auth-card__switch'>
						<Link to='/reset-password'>¿Olvidaste tu contraseña?</Link>
					</p>
				</Form>

				<div className='auth-footer'>
					{isHomePageEnabled() && (
						<p>
							{appName} es una plataforma de gestión contable y financiera que permite
							administrar cuentas, créditos, deudas, pagos, contratos, activos y reportes
							de forma centralizada, simplificando el control administrativo y financiero
							de tu negocio.
						</p>
					)}
					{(isTermsOfServiceEnabled() || isPrivacyPolicyEnabled()) && (
						<p className='auth-footer__links'>
							{isTermsOfServiceEnabled() && <Link to='/terms-of-service'>Términos y condiciones</Link>}
							{isTermsOfServiceEnabled() && isPrivacyPolicyEnabled() && ' · '}
							{isPrivacyPolicyEnabled() && <Link to='/privacy-policy'>Política de privacidad</Link>}
						</p>
					)}
				</div>
			</div>
		</div>
	);
}
